// src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { collection, doc, getDoc, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { useLocation } from 'react-router-dom';
import { AppRole, ADMIN_ROLE, STANDARD_ROLE, normalizeRole } from '../constants/roles';

// 認証コンテキストの型定義
interface AuthContextType {
  currentUser: User | null;
  userRole: AppRole | null;
  displayName: string | null;
  companyId: string | null;
  orgId: string | null;
  companyName: string | null;
  orgName: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  error: string | null;
  isPublicPath: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<AppRole | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = getAuth();
  const location = useLocation();

  // ★★★ isPublicPath関連のロジック（省略されていましたがこちらに含めます）★★★
  const checkIsPublicPath = (path: string): boolean => {
    const normalizedPath = path.replace(/^#/, '') || '/';
    const publicPaths: (string | RegExp)[] = [
      '/login',
      '/unauthorized',
      '/album-import',
    ];
    return publicPaths.some((publicPath) => 
      typeof publicPath === "string" 
        ? normalizedPath.startsWith(publicPath) 
        : publicPath.test(normalizedPath)
    );
  };
  const currentHashPath = location.hash.replace(/^#/, "") || "/";
  const isPublicPath = checkIsPublicPath(currentHashPath);
  // ★★★ ここまで ★★★

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        try {
          const userDocRef = doc(db, 'loginusers', user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const currentRole = normalizeRole(userData.role);
            const currentCompanyId = userData.companyId || null;
            const currentOrgId = userData.orgID || null;
            const currentDisplayName =
              (typeof userData.displayName === 'string' && userData.displayName.trim()) ||
              (typeof userData.userName === 'string' && userData.userName.trim()) ||
              user.displayName ||
              null;

            setUserRole(currentRole);
            setDisplayName(currentDisplayName);
            setCompanyId(currentCompanyId);
            setOrgId(currentOrgId);

            // 会社名と組織名を取得
            if (currentCompanyId) {
              const companyQuery = query(collection(db, 'companies'), where('companyID', '==', currentCompanyId));
              const companySnapshot = await getDocs(companyQuery);
              setCompanyName(companySnapshot.empty ? null : companySnapshot.docs[0].data().name);
            } else {
              setCompanyName(null);
            }

            if (currentOrgId) {
              const orgQuery = query(collection(db, 'organizations'), where('orgID', '==', currentOrgId));
              const orgSnapshot = await getDocs(orgQuery);
              setOrgName(orgSnapshot.empty ? null : orgSnapshot.docs[0].data().name);
            } else {
              setOrgName(null);
            }
          } else {
            // ユーザー情報がない場合は全てクリア
            setUserRole(STANDARD_ROLE);
            setDisplayName(user.displayName || null);
            setCompanyId(null); setOrgId(null);
            setCompanyName(null); setOrgName(null);
          }
        } catch (err) {
          console.error('ユーザー情報の取得エラー:', err);
          setUserRole(STANDARD_ROLE);
          setDisplayName(user.displayName || null);
          setCompanyId(null); setOrgId(null);
          setCompanyName(null); setOrgName(null);
        }
      } else {
        // ログアウト時は全ての情報をクリア
        setUserRole(null);
        setDisplayName(null);
        setCompanyId(null); setOrgId(null);
        setCompanyName(null); setOrgName(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  // ★★★ login, logout関数（省略されていましたがこちらに含めます）★★★
  const login = async (email: string, password: string) => {
    setError(null);
    setLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const userDoc = await getDoc(doc(db, 'loginusers', credential.user.uid));
      const role = userDoc.exists()
        ? normalizeRole(userDoc.data()?.role)
        : STANDARD_ROLE;

      if (role !== ADMIN_ROLE) {
        await signOut(auth);
        const err = new Error('管理者権限がありません。管理Webは admin ロールのユーザーのみ利用できます。');
        (err as Error & { code?: string }).code = 'auth/insufficient-role';
        setError(err.message);
        throw err;
      }

      // 管理Webへのログイン成功時に最終ログイン日時を更新
      if (userDoc.exists()) {
        try {
          await updateDoc(doc(db, 'loginusers', credential.user.uid), {
            lastLoginAt: Date.now(),
          });
        } catch (updateErr) {
          console.error('最終ログイン日時の更新エラー:', updateErr);
        }
      }
    } catch (err: any) {
      if (err?.code !== 'auth/insufficient-role') {
        setError(err.message || 'ログインに失敗しました');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setError(null);
    try {
      await signOut(auth);
    } catch (err: any) {
      setError(err.message || 'ログアウトに失敗しました');
      throw err;
    }
  };
  // ★★★ ここまで ★★★

  const value = {
    currentUser,
    userRole,
    displayName,
    companyId,
    orgId,
    companyName,
    orgName,
    login,
    logout,
    loading,
    error,
    isPublicPath
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};